import PropTypes from 'prop-types';
import { theme } from '@febos-io/design';

import { useMemo } from 'react';
import SignInPage from '../pages/SignInPage';
import ChangePasswordPage from '../pages/ChangePasswordPage';
import Error401Page from '../pages/Error401Page';
import UserErrorBoundary from '../components/AuthErrorBoundary';
import {
  ACTIVATION_MODE,
  CHANGE_PASSWORD_MODE,
  CLINIC_SELECTOR,
  COMPANY_SELECTOR,
  RENEW_PASSWORD_MODE,
  SIGN_IN_EMAIL_PASSWORD_MODE,
  USER_LOADING,
  VERIFYING_MODE
} from '../config';
import { useAuthContext } from '../providers';
import CompanySelectorPage from '../pages/CompanySelectorPage';
import ClinicSelectorPage from '../pages/ClinicSelectorPage';
import RequireUser from './RequireUser';
import Error403Page from '../pages/Error403Page';
import SignInVerificationPage from '../pages/SignInVerificationPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import SignOutPage from '../pages/SignOutPage';
import SignInInfoLoading from '../pages/SignInInfoLoading';

export function RoutesHandler({
  signInPage = <SignInPage />,
  signOutPage = <SignOutPage />,
  activationPage = <SignInPage />,
  changePasswordPage = <ChangePasswordPage />,
  renewPasswordPage = <ResetPasswordPage />,
  verifyingPage = <SignInVerificationPage />,
  routes = [],
}) {
  const {
    signInPathname,
    signOutPathname,
    activatePathname,
    changePasswordPathname,
    renewPasswordPathname,
    verifyingRedirectPathname,
    enableModes,
  } = useAuthContext();

  const authRoutes = useMemo(() => {
    const completeRoutes = [...routes];
    if (enableModes.includes(SIGN_IN_EMAIL_PASSWORD_MODE)) {
      completeRoutes.push({
        path: signInPathname,
        element: signInPage,
      });
      completeRoutes.push({
        path: signOutPathname,
        element: signOutPage,
      });
    }
    if (enableModes.includes(ACTIVATION_MODE)) {
      completeRoutes.push({
        path: activatePathname,
        element: activationPage,
      });
    }
    if (enableModes.includes(RENEW_PASSWORD_MODE)) {
      completeRoutes.push({
        path: renewPasswordPathname,
        element: renewPasswordPage,
      });
    }
    if (enableModes.includes(CHANGE_PASSWORD_MODE)) {
      completeRoutes.push({
        path: changePasswordPathname,
        element: changePasswordPage,
      });
    }
    if (enableModes.includes(VERIFYING_MODE)) {
      completeRoutes.push({
        path: verifyingRedirectPathname,
        element: verifyingPage,
      });
    }
    completeRoutes.push({
      path: USER_LOADING,
      element: (
        <RequireUser>
          <theme.layouts.BlankLayout />
        </RequireUser>
      ),
      children: [
        {
          index: true,
          element: <SignInInfoLoading />,
        }
      ],
    });
    completeRoutes.push({
      path: COMPANY_SELECTOR,
      element: (
        <RequireUser>
          <theme.layouts.BlankLayout />
        </RequireUser>
      ),
      children: [
        {
          index: true,
          element: <CompanySelectorPage />,
        }
      ],
    });
    completeRoutes.push({
      path: CLINIC_SELECTOR,
      element: (
        <RequireUser>
          <theme.layouts.BlankLayout />
        </RequireUser>
      ),
      children: [
        {
          index: true,
          element: <ClinicSelectorPage />,
        }
      ],
    });
    completeRoutes.push({
      path: '403',
      element: <Error403Page />,
    });
    completeRoutes.push({
      path: '*',
      element: <Error401Page />,
    });
    return completeRoutes;
  }, [
    routes,
    enableModes,
    signInPathname,
    signInPage,
    signOutPathname,
    signOutPage,
    activatePathname,
    activationPage,
    renewPasswordPathname,
    renewPasswordPage,
    changePasswordPathname,
    changePasswordPage,
    verifyingRedirectPathname,
    verifyingPage
  ]);
  return (
    <UserErrorBoundary>
      <theme.router.Router routes={authRoutes} />
    </UserErrorBoundary>
  );
}

RoutesHandler.propTypes = {
  signInPage: PropTypes.any,
  signOutPage: PropTypes.any,
  activationPage: PropTypes.any,
  verifyingPage: PropTypes.any,
  changePasswordPage: PropTypes.any,
  renewPasswordPage: PropTypes.any,
  routes: PropTypes.array,
};
